'use client'

import { useControlled } from '@base-ui/utils/useControlled'
import { Button } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { type LucideIcon, XIcon } from 'lucide-react'
import { type ComponentProps, createContext, type HTMLAttributes, useContext } from 'react'

type BannerContextProps = {
  setShow: (show: boolean) => void
  show: boolean
}

export const BannerContext = createContext<BannerContextProps>({
  setShow: () => {},
  show: true,
})

export type BannerProps = HTMLAttributes<HTMLDivElement> & {
  defaultVisible?: boolean
  inset?: boolean
  onClose?: () => void
  visible?: boolean
}

export const Banner = ({
  children,
  className,
  defaultVisible = true,
  inset = false,
  onClose,
  visible,
  ...props
}: BannerProps) => {
  const [show, setShow] = useControlled<boolean>({
    controlled: visible,
    default: defaultVisible,
    name: 'Banner',
    state: 'visible',
  })
  const setBannerVisibility = (nextShow: boolean) => {
    if (!nextShow) {
      onClose?.()
    }
    setShow(nextShow)
  }

  if (!show) {
    return null
  }

  return (
    <BannerContext.Provider value={{ setShow: setBannerVisibility, show }} data-sentry-element="BannerContext.Provider" data-sentry-component="Banner" data-sentry-source-file="index.tsx">
      <div
        className={cn(
          'flex w-full items-center justify-between gap-2 bg-primary px-4 py-3 text-primary-foreground',
          inset && 'rounded-lg',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </BannerContext.Provider>
  )
}

export type BannerIconProps = HTMLAttributes<HTMLDivElement> & {
  icon: LucideIcon
}

export const BannerIcon = ({ className, icon: Icon, ...props }: BannerIconProps) => (
  <div
    className={cn(
      'rounded-full border border-background/20 bg-background/10 p-1 shadow-sm',
      className,
    )}
    {...props}
  data-sentry-component="BannerIcon" data-sentry-source-file="index.tsx">
    <Icon size={16} data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
  </div>
)

export type BannerTitleProps = HTMLAttributes<HTMLParagraphElement>

export const BannerTitle = ({ className, ...props }: BannerTitleProps) => (
  <p className={cn('flex-1 text-sm', className)} {...props} data-sentry-component="BannerTitle" data-sentry-source-file="index.tsx" />
)

export type BannerActionProps = ComponentProps<typeof Button>

export const BannerAction = ({
  className,
  size = 'sm',
  variant = 'outline',
  ...props
}: BannerActionProps) => (
  <Button
    className={cn(
      'shrink-0 bg-transparent hover:bg-background/10 hover:text-background',
      className,
    )}
    size={size}
    variant={variant}
    {...props}
  data-sentry-element="Button" data-sentry-component="BannerAction" data-sentry-source-file="index.tsx" />
)

export type BannerCloseProps = ComponentProps<typeof Button>

export const BannerClose = ({
  className,
  onClick,
  size = 'icon',
  variant = 'ghost',
  ...props
}: BannerCloseProps) => {
  const { setShow } = useContext(BannerContext)

  return (
    <Button
      className={cn(
        'shrink-0 bg-transparent hover:bg-background/10 hover:text-background',
        className,
      )}
      onClick={e => {
        setShow(false)
        onClick?.(e)
      }}
      size={size}
      variant={variant}
      {...props}
    data-sentry-element="Button" data-sentry-component="BannerClose" data-sentry-source-file="index.tsx">
      <XIcon size={18} data-sentry-element="XIcon" data-sentry-source-file="index.tsx" />
    </Button>
  )
}
